import * as z from "zod"
import { shouldIncludeAttributes } from "./markdown-options"

export type MarkdownAttributes = Record<string, unknown>

export function parseTrailingMarkdownAttributes(value: string): {
  content: string
  attributes: MarkdownAttributes | null
} {
  const trimmed = value.trimEnd()
  const open = trimmed.lastIndexOf("<!--")
  if (open === -1 || !trimmed.endsWith("-->")) return { content: value, attributes: null }

  try {
    const raw: unknown = JSON.parse(trimmed.slice(open + "<!--".length, -"-->".length))
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
      return { content: value, attributes: null }
    }
    return { content: trimmed.slice(0, open).trimEnd(), attributes: raw as MarkdownAttributes }
  } catch {
    return { content: value, attributes: null }
  }
}

// An empty array or object carries nothing a reader or a re-parse can use, so it
// is dropped like null and "" — the schema puts the same default back.
function isEmptyAttribute(value: unknown): boolean {
  if (value === undefined || value === null || value === false || value === "") return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === "object") return Object.keys(value as object).length === 0
  return false
}

export function formatJsonAttributes(attributes: Record<string, unknown>): string {
  const cleaned: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(attributes)) {
    if (!isEmptyAttribute(v)) cleaned[k] = v
  }
  if (Object.keys(cleaned).length === 0) return ""
  // Escaping "<" and ">" keeps a string value from closing the comment early.
  const json = JSON.stringify(cleaned).replaceAll("<", "\\u003c").replaceAll(">", "\\u003e")
  return `<!-- ${json} -->`
}

export function formatMarkdownAttributes(schema: z.ZodType, data: unknown): string {
  if (!shouldIncludeAttributes()) return ""
  const parsed = schema.safeParse(data)
  return parsed.success ? formatJsonAttributes(parsed.data as Record<string, unknown>) : ""
}
